import { isProductFruitsEnabled } from './config';
import { ProductFruitsMessaging } from './messaging';
import { ProductFruitsUserSync } from './user-sync';

const CHATLYN_NAMESPACE_KEY = '__chatlyn';

const IDLE_CALLBACK_TIMEOUT_MS = 2000;

/**
 * Apps that support ProductFruits tours
 * Only apps listed here will receive tour initialization
 */
const APPS_WITH_TOURS = new Set(['phoenix', 'chatlyn-shell']);

export const shouldInitToursForApp = (appName, userData) => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (!isProductFruitsEnabled()) {
    return false;
  }

  if (!userData) {
    return false;
  }

  if (userData.preferences?.productFruitsOptOut) {
    return false;
  }

  return APPS_WITH_TOURS.has(appName);
};

export const scheduleProductFruitsInit = (callback) => {
  if (typeof window === 'undefined' || typeof callback !== 'function') {
    return undefined;
  }

  if (
    'requestIdleCallback' in window &&
    typeof window.requestIdleCallback === 'function'
  ) {
    return window.requestIdleCallback(() => callback(), {
      timeout: IDLE_CALLBACK_TIMEOUT_MS,
    });
  }

  return window.setTimeout(() => callback(), IDLE_CALLBACK_TIMEOUT_MS);
};

export const cancelScheduledProductFruitsInit = (handle) => {
  if (typeof window === 'undefined' || typeof handle !== 'number') {
    return;
  }

  if (
    'cancelIdleCallback' in window &&
    typeof window.cancelIdleCallback === 'function'
  ) {
    window.cancelIdleCallback(handle);
    return;
  }

  window.clearTimeout(handle);
};

export class ProductFruitsCoordinator {
  constructor(options = {}) {
    this.iframes = new Map();
    this.readyIframes = new Set();
    this.lastKnownPath = undefined;
    this.broadcastScheduled = false;
    this.broadcastHandle = undefined;

    this.options = {
      shouldInitToursForApp:
        options.shouldInitToursForApp || shouldInitToursForApp,
      scheduleProductFruitsInit:
        options.scheduleProductFruitsInit || scheduleProductFruitsInit,
      cancelScheduledProductFruitsInit:
        options.cancelScheduledProductFruitsInit ||
        cancelScheduledProductFruitsInit,
    };

    // Initialize specialized modules
    this.userSync = new ProductFruitsUserSync();
    this.messaging = new ProductFruitsMessaging({
      onIframeReady: this.handleIframeReady.bind(this),
      onTourEvent: this.handleTourEvent.bind(this),
      onUserPropertyUpdate: this.handleUserPropertyUpdate.bind(this),
    });
  }

  destroy() {
    this.messaging.destroy();

    if (this.broadcastHandle) {
      this.options.cancelScheduledProductFruitsInit(this.broadcastHandle);
      this.broadcastHandle = undefined;
    }

    this.iframes.clear();
    this.readyIframes.clear();
    this.broadcastScheduled = false;
  }

  setUserData(user) {
    if (!user) {
      return;
    }

    this.userSync.setUserData(user);
    this.scheduleUserBroadcast();
  }

  handleIframeLoad(iframeEl, appName) {
    if (!iframeEl?.contentWindow) {
      return;
    }

    this.iframes.set(appName, iframeEl.contentWindow);

    if (this.readyIframes.has(appName)) {
      this.sendUserAuthenticated(appName);
    }
  }

  pageChanged(path) {
    this.lastKnownPath = path;
    const productFruitsInstance = getProductFruitsInstance();
    if (productFruitsInstance?.pageChanged) {
      productFruitsInstance.pageChanged(path);
    }
  }

  updateUserProperties(props) {
    if (!props) {
      return;
    }

    this.userSync.updateProperties(props);
    this.messaging.broadcastPropertyUpdate(this.iframes, props);
  }

  scheduleUserBroadcast() {
    // Always cancel any pending broadcast when new user data arrives
    // This ensures we always broadcast the latest user data
    if (this.broadcastHandle) {
      this.options.cancelScheduledProductFruitsInit(this.broadcastHandle);
      this.broadcastHandle = undefined;
    }

    // Reset flag and schedule new broadcast with latest data
    this.broadcastScheduled = true;
    const handle = this.options.scheduleProductFruitsInit(() => {
      this.broadcastScheduled = false;
      this.broadcastHandle = undefined;
      this.broadcastUserData();
    });

    if (typeof handle === 'number') {
      this.broadcastHandle = handle;
      return;
    }

    // Scheduling failed (likely in non-browser env); broadcast synchronously.
    this.broadcastScheduled = false;
    this.broadcastUserData();
  }

  removeIframe(appName) {
    const removed = this.iframes.delete(appName);
    this.readyIframes.delete(appName);

    if (removed) {
      this.emitDebugEvent('iframe-removed', { appName });
    }
  }

  handleIframeReady(appName, sourceWindow) {
    if (!appName || !sourceWindow) {
      return;
    }

    this.emitDebugEvent('iframe-ready', { appName });
    this.readyIframes.add(appName);
    this.iframes.set(appName, sourceWindow);
    this.sendUserAuthenticated(appName);
  }

  sendUserAuthenticated(appName) {
    const iframeWindow = this.iframes.get(appName);
    if (!iframeWindow) {
      return;
    }

    const completeUserData = this.userSync.getCompleteUserData();
    const userData = this.userSync.getUserData();
    if (!completeUserData || !userData) {
      return;
    }

    const shouldInit = this.options.shouldInitToursForApp(appName, userData);

    try {
      this.messaging.sendUserAuthenticated(
        iframeWindow,
        completeUserData,
        shouldInit
      );
    } catch (error) {
      this.removeIframe(appName);
      captureWithSentry(error, {
        contexts: {
          productfruits: {
            appName,
            phase: 'sendUserAuthenticated',
            error: String(error),
          },
        },
      });
    }
  }

  broadcastUserData() {
    const userData = this.userSync.getUserData();
    if (!userData) {
      return;
    }
    this.iframes.forEach((_iframe, appName) =>
      this.sendUserAuthenticated(appName)
    );
  }

  handleUserPropertyUpdate(props, source) {
    this.userSync.updateProperties(props);
    this.messaging.broadcastPropertyUpdate(this.iframes, props, source);
  }

  handleTourEvent(message) {
    this.messaging.broadcastTourEvent(this.iframes, message);
  }

  emitDebugEvent(eventName, payload = {}) {
    if (typeof window === 'undefined') {
      return;
    }

    window.dispatchEvent(
      new CustomEvent('productfruits:debug', {
        detail: {
          eventName,
          lastKnownPath: this.lastKnownPath,
          ...payload,
        },
      })
    );
  }
}

const getHostWindow = () => window;

const getProductFruitsInstance = () => {
  const hostWindow = getHostWindow();
  const instance = hostWindow.productFruits;
  if (instance && typeof instance === 'object') {
    return instance;
  }
  return undefined;
};

const captureWithSentry = (error, context) => {
  const sentry = getHostWindow().Sentry;
  sentry?.captureException?.(error, context);
};

export default ProductFruitsCoordinator;

